import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ResidenceCardPreview,
  ResidenceCardStyled,
  ResidenceContent,
  ResidencePrice,
  ResidenceTitle
} from './styled';
import SvgIconLocation from '../../../public/static/images/residences/icon-location.svg';
import SvgIconDeadline from '../../../public/static/images/residences/icon-key.svg';
import SvgIconMetro from '../../../public/static/images/residences/metro.svg';
import SvgIconBrand from '../../../public/static/images/residences/brand.svg';
import { ResidenceParam } from './ResidenceParam';
import useScreenSize from '../../../hooks/useScreenSize';
import { CurrencySymbol } from '../../ui/ItemCard/CurrencySymbol';
import { CardPreviewImage } from '../../ui/ItemCard/styled';
import { useClient } from '../../../hooks/useClient';
import { Residence } from '../../../graphql/types';

export interface ResidenceCardProps extends Residence {
  hideExcessItems?: boolean;
  showPerUnit?: boolean;
  cypress?: string;
  stat?: string;
}

export const ResidenceCard: FC<ResidenceCardProps> = ({
  path,
  coverUrl,
  price,
  name,
  shortAddress,
  address,
  deadline,
  cypress,
  metroName,
  companyName,
  hideExcessItems = false,
  showPerUnit,
  stat = 'residence-card-link'
}) => {
  const { isSmall, isSmallDesktop, isLarge } = useScreenSize();
  const isClient = useClient();
  const { t } = useTranslation();

  const shownPrice = showPerUnit ? price.perSqm : price.total?.minimum;
  const formatPrice = shownPrice?.toLocaleString().replace(/,/g, ' ');
  const priceValue = shownPrice?.toLocaleString('ru-RU');

  const fontSizes = {
    price: 'h2',
    title: 'h3'
  };

  const cardLinkProps =
    isClient && (isSmallDesktop || isLarge) ? { target: '_blank' } : {};

  const hideOnMobile = (!isSmall && isClient) || !isClient;

  return (
    <ResidenceCardStyled
      $hideExcessItems={hideExcessItems}
      href={path}
      className="item-card_residence"
      cypress={cypress}
      stat={stat}
      {...cardLinkProps}
    >
      <ResidenceCardPreview>
        <CardPreviewImage
          src={coverUrl}
          alt={t('sections.residences.img_alt', { name })}
          loading="lazy"
          width={100}
          height={75}
        />
      </ResidenceCardPreview>
      <ResidenceContent>
        <ResidencePrice tag={fontSizes.price} data-cy={`${cypress}-price`}>
          <Trans
            i18nKey="sections.residences.from"
            values={{ price: priceValue }}
            components={{
              0: <span>{priceValue}</span>,
              1: <CurrencySymbol currency={price.currency} />
            }}
          >
            <span>{formatPrice}</span>
            <CurrencySymbol currency={price.currency} />
          </Trans>
          {showPerUnit && <span>{t('residences.per_unit')}</span>}
        </ResidencePrice>
        <ResidenceTitle tag={fontSizes.title}>{name}</ResidenceTitle>
        {hideOnMobile && companyName && (
          <ResidenceParam
            icon={<SvgIconBrand />}
            param={companyName}
            hideOnMobile
          />
        )}
        <ResidenceParam
          icon={<SvgIconLocation />}
          param={shortAddress ? `${shortAddress}` : `${address}`}
        />
        {hideOnMobile && metroName && (
          <ResidenceParam
            icon={<SvgIconMetro />}
            param={metroName}
            hideOnMobile
          />
        )}
        {deadline && (
          <ResidenceParam icon={<SvgIconDeadline />} param={deadline} />
        )}
      </ResidenceContent>
    </ResidenceCardStyled>
  );
};
